import axios from 'axios';
import { Preferences } from '@capacitor/preferences';
import { API_URLs } from '../../constants/api_urls';
import { API_BASE_URL } from '../../constants/constants';

const IPWHO_URL = 'https://ipwho.is/';
const IPWHO_TIMEOUT_MS = 3000;
const DEFAULT_COUNTRY_CACHE_KEY = 'auth_default_country_iso';
const DEFAULT_COUNTRY_EXPIRY_KEY = 'auth_default_country_iso_expiry';
const DEFAULT_COUNTRY_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const FALLBACK_COUNTRY_ISO = 'IN';

type CountryListResponse = {
    default: string | null;
    items: { code: string; name: string }[];
};

type IpWhoResponse = {
    success?: boolean;
    country_code?: string;
};

const isValidIso2 = (value: string | undefined | null): value is string =>
    !!value && /^[a-zA-Z]{2}$/.test(value);

export const fetchAllowedCountries = async (): Promise<string[]> => {
    const response = await axios.get<CountryListResponse>(`${API_BASE_URL}${API_URLs.COUNTRY}`, { timeout: 5000 });
    const items = response.data?.items ?? [];
    return items
        .map((item) => item.code?.toUpperCase())
        .filter(isValidIso2);
};

const loadCachedCountryIso = async (): Promise<string | null> => {
    const { value } = await Preferences.get({ key: DEFAULT_COUNTRY_CACHE_KEY });
    const { value: expiryStr } = await Preferences.get({ key: DEFAULT_COUNTRY_EXPIRY_KEY });

    if (!value || !expiryStr) return null;

    if (Date.now() > Number(expiryStr)) {
        await Preferences.remove({ key: DEFAULT_COUNTRY_CACHE_KEY });
        await Preferences.remove({ key: DEFAULT_COUNTRY_EXPIRY_KEY });
        return null;
    }

    return isValidIso2(value) ? value.toUpperCase() : null;
};

const saveCachedCountryIso = async (iso: string) => {
    await Preferences.set({ key: DEFAULT_COUNTRY_CACHE_KEY, value: iso.toUpperCase() });
    await Preferences.set({
        key: DEFAULT_COUNTRY_EXPIRY_KEY,
        value: String(Date.now() + DEFAULT_COUNTRY_TTL_MS),
    });
};

const fetchCountryIsoFromIpWho = async (): Promise<string | null> => {
    try {
        const { data } = await axios.get<IpWhoResponse>(IPWHO_URL, { timeout: IPWHO_TIMEOUT_MS });
        if (!data?.success || !isValidIso2(data.country_code)) {
            console.warn('IPWho.is returned an invalid country response', data);
            return null;
        }
        return data.country_code.toUpperCase();
    } catch (error) {
        console.warn('IPWho.is lookup failed', error);
        return null;
    }
};

export const getDefaultCountryIso = async (isAuthenticated: boolean): Promise<string | null> => {
    if (isAuthenticated) return null;

    const cached = await loadCachedCountryIso();
    if (cached) return cached;

    const iso = await fetchCountryIsoFromIpWho();
    if (!iso) return null;

    await saveCachedCountryIso(iso);
    return iso;
};

export const resolveDefaultCountry = (allowed: string[], preferred?: string | null): string => {
    const allowedUpper = allowed.map((code) => code.toUpperCase());
    if (preferred && allowedUpper.indexOf(preferred.toUpperCase()) !== -1) {
        return preferred.toUpperCase();
    }
    if (allowedUpper.indexOf(FALLBACK_COUNTRY_ISO) !== -1) {
        return FALLBACK_COUNTRY_ISO;
    }
    return allowedUpper[0] ?? FALLBACK_COUNTRY_ISO;
};
